// src/components/AISuggestionCard.js — Sugestão de resposta da IA no ChatPage.
//
// Princípio inegociável do produto: a IA NUNCA envia sozinha. Este card é o
// portão entre a máquina e o botão de enviar. Toda decisão visual aqui
// existe pra deixar isso óbvio pro agente em 200ms de olhada.
//
// SIGNATURE VISUAL — "bolha não enviada":
//   A sugestão não é renderizada como texto de card genérico. Ela usa a
//   MESMA geometria da bolha OUTBOUND real da thread (alinhada à direita,
//   canto inferior direito de 3px), só que roxa em vez de verde e com uma
//   linha de status que diz "não enviado" onde as bolhas de verdade
//   carregam ✓✓. O agente vê exatamente o que vai cair no WhatsApp do
//   cliente e, ao mesmo tempo, vê que ainda não caiu. O gate do produto
//   virou geometria, não selo decorativo.
//
// Fora isso, disciplina: cores chapadas, uma sombra, uma transição de
// 150ms. Sem gradiente, sem ícone animado, sem confete de IA.
//
// Ciclo de vida (o card é dono do log_id, então é dono do PATCH):
//   open=true       → POST /ai/suggest-reply  (AbortController no cleanup)
//   [Usar e enviar] → onUseText(texto) + PATCH action=USED
//   [Editar]        → onEditText(texto) + PATCH action=EDITED (sem
//                     final_text — registra a INTENÇÃO de editar; se o
//                     agente abandonar o rascunho, ainda foi uma edição)
//   [Descartar] / ESC / clique fora / fechar sem ação → PATCH DISCARDED
//
// O PATCH de descarte roda no cleanup do effect, então cobre também o caso
// do ChatPage fechar o card sozinho (auto-invalidação quando o agente
// começa a digitar) e o unmount da página.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FiCpu, FiX, FiAlertTriangle, FiFileText, FiImage } from 'react-icons/fi';
import api from '../services/Api';

const SYNE = "'Syne', system-ui, sans-serif";
const OUTFIT = "'Outfit', system-ui, sans-serif";

// Accent da feature IA — mesmo par usado pela aba Automação → IA Assistida.
const AI = {
  main: '#893FED',
  mainD: '#6D2FBB',
  soft: '#E2DCF7',
  softer: '#F4F1FE',
};
// Tokens herdados do ChatPage (não inventar cor nova pra estados).
const C = {
  card: '#FFFFFF', bdr: '#E2E8F0',
  text: '#1A2535', text2: '#4B5E72', text3: '#94A3B8',
  amber: '#D97706', amberD: '#92400E', amberBg: '#FFFBEB', amberB: '#FDE68A',
  red: '#DC2626', redD: '#7F1D1D', redBg: '#FEF2F2', redB: '#FECACA',
};

const GUTTER = 12;      // respiro mínimo das bordas da viewport
const MAX_WIDTH = 400;
const ARROW = 8;

const clamp = (v, min, max) => Math.max(min, Math.min(v, max));

// ─── Tradução de erro do backend → copy acionável ──────────────────────────
// Regra de escrita: o erro diz o que aconteceu e o que fazer. Não pede
// desculpa e não é vago. `link` leva pra tela que resolve.
function readError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const retryAfter = Number(err?.response?.headers?.['retry-after']) || 60;

  if (status === 402) {
    return {
      status,
      title: 'IA não configurada',
      message: detail || 'Conecte uma chave de IA para gerar sugestões.',
      link: 'Abrir Automação → IA Assistida',
    };
  }
  if (status === 401) {
    return {
      status,
      title: 'Chave recusada pelo provedor',
      message: 'A conexão com a IA foi desativada. Conecte a chave de novo para voltar a sugerir.',
      link: 'Abrir Automação → IA Assistida',
    };
  }
  if (status === 429) {
    return { status, title: 'Limite de sugestões atingido', message: '', retryAfter };
  }
  if (status === 404) {
    return {
      status,
      title: 'Conversa não encontrada',
      message: 'Recarregue a página e abra a conversa de novo.',
    };
  }
  if (status === 502) {
    return {
      status,
      title: 'O provedor de IA não respondeu',
      message: detail || 'Tente de novo em alguns segundos.',
    };
  }
  if (status === 503) {
    return {
      status,
      title: 'IA indisponível no servidor',
      message: detail || 'Fale com o suporte da Cetto.',
    };
  }
  if (status === 400) {
    return { status, title: 'Não foi possível gerar', message: detail || 'Requisição inválida.' };
  }
  return {
    status,
    title: 'Não foi possível gerar a sugestão',
    message: detail || 'Verifique a conexão e tente de novo.',
  };
}

// Metadados em pt-BR: "claude-sonnet-5  ·  1,8 s  ·  823 tokens".
function formatMeta(data) {
  if (!data) return '';
  const parts = [];
  if (data.model) parts.push(data.model);
  if (typeof data.latency_ms === 'number') {
    parts.push(`${(data.latency_ms / 1000).toFixed(1).replace('.', ',')} s`);
  }
  if (typeof data.tokens_used === 'number') {
    parts.push(`${data.tokens_used.toLocaleString('pt-BR')} tokens`);
  }
  return parts.join('  ·  ');
}

export default function AISuggestionCard({
  open,
  onClose,
  clientId,
  onUseText,
  onEditText,
  anchorRect,
}) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading');   // loading | ready | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [entered, setEntered] = useState(false);

  const cardRef = useRef(null);
  // logId + action vivem em ref porque o cleanup do effect precisa lê-los no
  // valor MAIS RECENTE (state seria capturado stale no closure).
  const logIdRef = useRef(null);
  const actionRef = useRef(null);

  const patchAction = useCallback((action, finalText) => {
    const logId = logIdRef.current;
    if (!logId || actionRef.current) return;
    actionRef.current = action;
    const body = { action };
    if (action === 'EDITED' && finalText) body.final_text = finalText;
    // Fire-and-forget: telemetria não pode travar o atendimento.
    api.patch(`/ai/suggest-reply/${logId}/action`, body).catch(() => {});
  }, []);

  // ─── Ciclo principal: pede a sugestão, descarta no cleanup ───────────────
  useEffect(() => {
    if (!open || !clientId) return undefined;

    const ctrl = new AbortController();
    logIdRef.current = null;
    actionRef.current = null;
    setPhase('loading');
    setData(null);
    setError(null);
    setCooldown(0);

    api
      .post('/ai/suggest-reply', { client_id: clientId }, { signal: ctrl.signal })
      .then((res) => {
        logIdRef.current = res.data?.log_id || null;
        setData(res.data);
        setPhase('ready');
      })
      .catch((err) => {
        if (ctrl.signal.aborted || err?.code === 'ERR_CANCELED') return;
        const parsed = readError(err);
        setError(parsed);
        if (parsed.status === 429) setCooldown(parsed.retryAfter);
        setPhase('error');
      });

    return () => {
      ctrl.abort();
      // Fechou sem decidir nada = descartou. Cobre ESC, clique fora,
      // auto-invalidação do ChatPage e unmount da página.
      if (logIdRef.current && !actionRef.current) {
        actionRef.current = 'DISCARDED';
        api
          .patch(`/ai/suggest-reply/${logIdRef.current}/action`, { action: 'DISCARDED' })
          .catch(() => {});
      }
      logIdRef.current = null;
    };
  }, [open, clientId]);

  // Entrada fade+slide-up de 150ms. Um frame de atraso pra o browser
  // registrar o estado inicial antes de transicionar.
  useLayoutEffect(() => {
    if (!open) { setEntered(false); return undefined; }
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Countdown do 429 — a copy só libera quando o limite expira.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // ESC + clique fora. Ambos caem no mesmo caminho de descarte.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    const onDown = (e) => {
      if (cardRef.current && cardRef.current.contains(e.target)) return;
      // O botão âncora é quem alterna o card. Se ele também fechasse por
      // "clique fora", o clique seguinte reabriria — e cada abertura é uma
      // chamada paga ao provedor. Deixa o toggle pro próprio botão.
      if (e.target.closest && e.target.closest('[data-ai-anchor]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    // O timeout de 0 evita que o próprio clique que abriu o card já feche.
    const id = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, onClose]);

  // Foco entra no card assim que ele aparece — ESC e Tab funcionam sem o
  // agente precisar clicar antes.
  useEffect(() => {
    if (open && cardRef.current) cardRef.current.focus({ preventScroll: true });
  }, [open, phase]);

  if (!open) return null;

  const handoff = !!(data && (data.handoff === true || data.confidence === '0.0'));
  const suggestion = data?.suggestion || '';
  // Arquivo que a IA pediu pra enviar junto. O token [ENVIAR:slug] já saiu
  // do texto no backend — aqui chega o arquivo resolvido, ou null.
  const arquivo = data?.asset || null;

  const handleUse = () => {
    patchAction('USED');
    // `arquivo` vai junto: quem envia é o ChatPage, pelo caminho de mídia
    // de sempre. Mandar só o texto faria a legenda chegar sem o anexo.
    onUseText(suggestion, arquivo);
    onClose();
  };
  const handleEdit = () => {
    // EDITED entra já no clique, sem final_text: registra a intenção. Se o
    // agente abandonar o rascunho, ainda foi uma sugestão editada.
    patchAction('EDITED');
    onEditText(suggestion);
    onClose();
  };
  const handleDiscard = () => {
    patchAction('DISCARDED');
    onClose();
  };

  // ─── Posicionamento: fixed ancorado ACIMA do botão ───────────────────────
  // getBoundingClientRect é relativo à viewport, então `fixed` é o par
  // correto (absolute exigiria matemática de offsetParent e derivaria no
  // scroll). Tudo clampado pra nunca sair da tela no mobile.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const width = Math.min(MAX_WIDTH, vw - GUTTER * 2);
  const anchorLeft = anchorRect ? anchorRect.left : vw / 2 - width / 2;
  const anchorTop = anchorRect ? anchorRect.top : vh - 120;
  const left = clamp(anchorLeft - 10, GUTTER, Math.max(GUTTER, vw - width - GUTTER));
  const bottom = clamp(vh - anchorTop + 10, GUTTER, vh - GUTTER);
  const arrowLeft = anchorRect
    ? clamp(anchorRect.left + anchorRect.width / 2 - left - ARROW, 14, Math.max(14, width - 30))
    : width / 2 - ARROW;

  const accent = handoff ? C.amber : AI.main;
  const border = phase === 'error' ? C.redB : handoff ? C.amberB : AI.soft;

  const headerLabel =
    phase === 'error' ? 'IA indisponível' : handoff ? 'Passar pra humano' : 'Sugestão da IA';

  return (
    <Box
      ref={cardRef}
      role="dialog"
      aria-label="Sugestão de resposta da IA"
      aria-live="polite"
      tabIndex={-1}
      sx={{
        position: 'fixed',
        left: `${left}px`,
        bottom: `${bottom}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(180, anchorTop - 24)}px`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1400,
        bgcolor: C.card,
        border: `1px solid ${border}`,
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(137,63,237,0.15)',
        outline: 'none',
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0)' : 'translateY(6px)',
        transition: 'opacity 150ms ease, transform 150ms ease',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          opacity: 1,
          transform: 'none',
        },
      }}
    >
      {/* Seta apontando pro botão que abriu o card */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: '-5px',
          left: `${arrowLeft}px`,
          width: ARROW, height: ARROW,
          bgcolor: C.card,
          borderRight: `1px solid ${border}`,
          borderBottom: `1px solid ${border}`,
          transform: 'rotate(45deg)',
        }}
      />

      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0,
        p: '11px 12px 9px 14px',
        borderBottom: `1px solid ${phase === 'error' ? C.redB : handoff ? C.amberB : AI.softer}`,
      }}>
        <Box sx={{ display: 'flex', color: phase === 'error' ? C.red : accent }}>
          {handoff ? <FiAlertTriangle size={13} /> : <FiCpu size={13} />}
        </Box>
        <Typography sx={{
          fontFamily: SYNE, fontSize: '0.72rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: phase === 'error' ? C.red : accent, flex: 1,
        }}>
          {headerLabel}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={handleDiscard}
          aria-label="Fechar sugestão"
          sx={{
            width: 22, height: 22, borderRadius: '5px', p: 0,
            border: 'none', bgcolor: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.text3, transition: 'all 0.15s',
            '&:hover': { bgcolor: AI.softer, color: C.text2 },
            '&:focus-visible': { outline: `2px solid ${AI.main}`, outlineOffset: 1 },
          }}
        >
          <FiX size={13} />
        </Box>
      </Box>

      {/* ─── Corpo ──────────────────────────────────────────────────────── */}
      <Box sx={{ p: '14px', overflowY: 'auto', flex: 1 }}>
        {phase === 'loading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 0.5 }}>
            <CircularProgress size={14} thickness={5} sx={{ color: C.text3 }} />
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.8rem', color: C.text2 }}>
              Gerando sugestão…
            </Typography>
          </Box>
        )}

        {phase === 'error' && <ErrorBody error={error} cooldown={cooldown} navigate={navigate} />}

        {phase === 'ready' && handoff && (
          <Box sx={{
            bgcolor: C.amberBg, border: `1px solid ${C.amberB}`,
            borderRadius: '8px', p: '11px 12px',
          }}>
            <Typography sx={{
              fontFamily: SYNE, fontSize: '0.82rem', color: C.amberD, lineHeight: 1.55,
            }}>
              A IA sugere passar pra humano — assunto complexo ou fora do escopo configurado.
            </Typography>
          </Box>
        )}

        {phase === 'ready' && !handoff && (
          <>
            {/* SIGNATURE: bolha outbound com a geometria da thread, roxa e
                marcada como não enviada. */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{
                maxWidth: '92%',
                bgcolor: AI.soft,
                borderRadius: '12px',
                borderBottomRightRadius: '3px',
                p: '10px 13px',
              }}>
                <Typography sx={{
                  fontFamily: SYNE, fontSize: '0.85rem', color: C.text,
                  lineHeight: 1.55, whiteSpace: 'pre-wrap',
                }}>
                  {suggestion}
                </Typography>

                {/* A IA pediu pra enviar um arquivo da biblioteca (out/26).
                    Aparece COLADO na sugestão porque é parte da mesma
                    resposta: o texto é a legenda, o anexo é o conteúdo.
                    Separar os dois faria o agente mandar a legenda sozinha.

                    A IA só sugere — o anexo é enviado pelo mesmo caminho de
                    mídia de sempre, com as travas de lá (janela de 24h,
                    chip pausado). Ver `asset` em SuggestReplyOut. */}
                {arquivo && (
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.9,
                    mt: 1, pt: 0.9, borderTop: `1px solid ${AI.main}33`,
                  }}>
                    <Box sx={{ color: AI.main, display: 'flex', flexShrink: 0 }}>
                      {arquivo.kind === 'IMAGE' ? <FiImage size={13} /> : <FiFileText size={13} />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{
                        fontFamily: SYNE, fontSize: '0.74rem', fontWeight: 700,
                        color: C.text, lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {arquivo.title}
                      </Typography>
                      <Typography sx={{
                        fontFamily: SYNE, fontSize: '0.64rem', color: C.text3, lineHeight: 1.3,
                      }}>
                        anexo sugerido pela IA
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
              gap: 0.5, mt: 0.6,
            }}>
              <Box sx={{
                width: 5, height: 5, borderRadius: '50%',
                border: `1px solid ${AI.main}`, opacity: 0.7,
              }} />
              <Typography sx={{
                fontFamily: OUTFIT, fontSize: '0.62rem', color: AI.main,
                letterSpacing: '0.02em', opacity: 0.85,
              }}>
                não enviado
              </Typography>
            </Box>
          </>
        )}

        {phase === 'ready' && (
          <Typography sx={{
            fontFamily: OUTFIT, fontSize: '0.62rem', color: C.text3, mt: 1.4,
          }}>
            {formatMeta(data)}
          </Typography>
        )}
      </Box>

      {/* ─── Ações ──────────────────────────────────────────────────────── */}
      {phase === 'ready' && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0,
          p: '0 14px 14px', flexWrap: 'wrap',
        }}>
          {!handoff && (
            <>
              <ActionButton variant="primary" onClick={handleUse}>
                Usar e enviar
              </ActionButton>
              <ActionButton variant="secondary" onClick={handleEdit}>
                Editar
              </ActionButton>
            </>
          )}
          <ActionButton variant="tertiary" onClick={handleDiscard}>
            Descartar
          </ActionButton>
        </Box>
      )}
    </Box>
  );
}

// ─── Corpo do estado de erro ───────────────────────────────────────────────
function ErrorBody({ error, cooldown, navigate }) {
  if (!error) return null;
  const isRateLimited = error.status === 429;
  return (
    <Box sx={{
      bgcolor: C.redBg, border: `1px solid ${C.redB}`,
      borderRadius: '8px', p: '11px 12px',
    }}>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.78rem', fontWeight: 700, color: C.redD, mb: 0.4,
      }}>
        {error.title}
      </Typography>
      <Typography sx={{
        fontFamily: SYNE, fontSize: '0.78rem', color: C.redD, lineHeight: 1.5, opacity: 0.9,
      }}>
        {isRateLimited
          ? (cooldown > 0
              ? `Aguarde ${cooldown} segundo${cooldown === 1 ? '' : 's'} para pedir outra sugestão.`
              : 'Você já pode pedir outra sugestão.')
          : error.message}
      </Typography>
      {error.link && (
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/automacao')}
          sx={{
            mt: 1, p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer',
            fontFamily: SYNE, fontSize: '0.74rem', fontWeight: 700, color: AI.main,
            textDecoration: 'underline', textUnderlineOffset: '2px',
            '&:hover': { color: AI.mainD },
            '&:focus-visible': { outline: `2px solid ${AI.main}`, outlineOffset: 2 },
          }}
        >
          {error.link}
        </Box>
      )}
    </Box>
  );
}

// ─── Botão de ação ─────────────────────────────────────────────────────────
// Três pesos: primary (envia), secondary (edita), tertiary (descarta). A
// hierarquia visual espelha o custo da ação — o que vai pro cliente é o
// único com preenchimento sólido.
function ActionButton({ variant, onClick, children }) {
  const styles = {
    primary: {
      bgcolor: AI.main, color: '#fff', border: `1px solid ${AI.main}`,
      '&:hover': { bgcolor: AI.mainD, borderColor: AI.mainD },
    },
    secondary: {
      bgcolor: 'transparent', color: AI.main, border: `1px solid ${AI.main}`,
      '&:hover': { bgcolor: AI.soft },
    },
    tertiary: {
      bgcolor: 'transparent', color: C.text3, border: '1px solid transparent',
      '&:hover': { color: C.text2 },
    },
  }[variant];

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.6,
        p: '7px 13px', borderRadius: '8px', cursor: 'pointer',
        fontFamily: SYNE, fontSize: '0.76rem', fontWeight: 700,
        letterSpacing: '0.01em', transition: 'all 0.15s',
        marginLeft: variant === 'tertiary' ? 'auto' : 0,
        '&:focus-visible': { outline: `2px solid ${AI.main}`, outlineOffset: 2 },
        ...styles,
      }}
    >
      {children}
    </Box>
  );
}
